
import {
  CareerSitePositionCategoriesResponse,
  CareerSiteService,
} from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCategoriesParams {
  accountSlug: string;
  brandId: string;
  query?: string;
  perPage?: number;
}

interface UseCategoriesReturn {
  data: CareerSitePositionCategoriesResponse['categories'];
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  pagination?: CareerSitePositionCategoriesResponse['pagination'];
}

export function useCategories({
  accountSlug,
  brandId,
  query = '',
  perPage = 50,
}: UseCategoriesParams): UseCategoriesReturn {
  const [data, setData] = useState<
    CareerSitePositionCategoriesResponse['categories']
  >([]);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] =
    useState<CareerSitePositionCategoriesResponse['pagination']>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(
    async (pageNum: number, searchQuery: string, reset = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      abortControllerRef.current = new AbortController();

      setIsFetching(true);

      try {
        const request =
          CareerSiteService.getInternalApiCareerSitePositionsCategories(
            {
              account_slug: accountSlug,
              brand_id: brandId,
            },
            searchQuery.trim() || undefined,
            pageNum,
            perPage,
          );

        const abortListener = () => request.cancel();
        abortControllerRef.current.signal.addEventListener(
          'abort',
          abortListener,
        );

        const result = (await request) as CareerSitePositionCategoriesResponse;

        abortControllerRef.current?.signal.removeEventListener(
          'abort',
          abortListener,
        );

        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        setPagination(result.pagination);

        if (reset) {
          setData(result.categories);
        } else {
          setData(prev => [...prev, ...result.categories]);
        }
      } catch {
        // fetch error — not re-thrown
      } finally {
        if (isMountedRef.current) {
          setIsFetching(false);
        }
      }
    },
    [accountSlug, brandId, perPage],
  );

  const loadMore = useCallback(() => {
    if (!isFetching && pagination && currentPage < pagination.total_pages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      void fetchData(nextPage, query, false);
    }
  }, [currentPage, fetchData, isFetching, pagination, query]);

  const refetch = useCallback(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);
  }, [fetchData, query]);

  const hasMore = pagination ? currentPage < pagination.total_pages : false;

  useEffect(() => {
    setCurrentPage(1);
    void fetchData(1, query, true);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, fetchData]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { data, isFetching, hasMore, loadMore, refetch, pagination };
}
